import { useState, useRef, useCallback, useImperativeHandle, useLayoutEffect, forwardRef, type KeyboardEvent } from "react";
import { Camera, X } from "lucide-react";
import { toast } from "sonner";
import { trackFeature, trackMessageForDau } from "@/lib/analytics";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import type { RealtimeVoiceState } from "@/hooks/useRealtimeVoice";

interface ChatInputProps {
  onSend: (
    message: string,
    imageDataOrDisplay?: { base64: string; mediaType: string } | string,
    images?: { base64: string; mediaType: string }[],
  ) => void;
  disabled: boolean;
  isStreaming?: boolean;
  onStop?: () => void;
  showNewChat?: boolean;
  onNewChat?: () => void;
  limitReached?: boolean;
  keyboardOpen?: boolean;
  /** True for Pro users (or admin+adminPreview god-mode). Shows voice buttons. */
  isProUser?: boolean;
  /** Current voice conversation state from useRealtimeVoice (in Index.tsx). */
  voiceModeState?: RealtimeVoiceState;
  /** Toggle the star voice conversation mode. */
  onVoiceModeToggle?: () => void;
  /** Deactivate voice mode when user taps the input overlay. */
  onDeactivateVoiceMode?: () => void;
}

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB per file
const MAX_IMAGES = 8; // matches PhotoIntakeModal's cap so the whole app is consistent
const MAX_TOTAL_BASE64 = 15 * 1024 * 1024; // ~15MB combined base64 backstop for the request body
const MAX_LONG_EDGE = 1568; // Anthropic downscales anything larger server-side anyway.

const readFileAsDataURL = (f: File | Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(f);
  });

// Downscale an image File so its long edge is at most MAX_LONG_EDGE, re-encode
// as JPEG q0.85, and return base64 + a data URL for preview. We were uploading
// full-res pixels the model never sees; this trims them before encoding.
// Preserves aspect ratio, skips the re-encode if already small enough, and on
// any decode/canvas failure falls back to the original file rather than dropping it.
async function downscaleImage(
  file: File,
): Promise<{ base64: string; mediaType: string; dataUrl: string }> {
  const originalDataUrl = await readFileAsDataURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = originalDataUrl;
    });

    const longEdge = Math.max(img.width, img.height);
    if (longEdge <= MAX_LONG_EDGE) {
      // Already small enough — keep original bytes, no re-encode.
      return {
        base64: originalDataUrl.split(",")[1],
        mediaType: file.type || "image/jpeg",
        dataUrl: originalDataUrl,
      };
    }

    const scale = MAX_LONG_EDGE / longEdge;
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no 2d context");
    ctx.drawImage(img, 0, 0, w, h);

    const scaledDataUrl = canvas.toDataURL("image/jpeg", 0.85);
    return {
      base64: scaledDataUrl.split(",")[1],
      mediaType: "image/jpeg",
      dataUrl: scaledDataUrl,
    };
  } catch {
    // Decode/canvas failed — fall back to the original file, don't drop it.
    return {
      base64: originalDataUrl.split(",")[1],
      mediaType: file.type || "image/jpeg",
      dataUrl: originalDataUrl,
    };
  }
}

const ChatInput = forwardRef<{ focus: () => void; setText: (text: string) => void }, ChatInputProps>(({ onSend, disabled, isStreaming, onStop, showNewChat, onNewChat, limitReached, keyboardOpen = false, isProUser = false, voiceModeState = "idle", onVoiceModeToggle, onDeactivateVoiceMode }, ref) => {
  const [isEmpty, setIsEmpty] = useState(true);

  // One-shot voice input for the inline mic icon (inserts transcript into editor, no auto-send)
  const voiceInput = useVoiceInput();

  // ⚠️ "error" is deliberately NOT voice mode, and this is load-bearing for
  // three separate defects that all came from treating it as ON (Sep 2 2026).
  //
  // By the time the hook reports "error" it has already run teardown() — mic
  // tracks stopped, peer connection closed, the voice_sessions row closed out.
  // Nothing is held open, so there is nothing to exit FROM. But the old
  // `!== "idle"` meant a failed connect left the input bar in voice mode, and:
  //
  //   1. Two surfaces for one failure. The hook toasts "Voice hiccupped — try
  //      again, or type instead 💜" AND this rendered the overlay reading
  //      "Voice hiccupped — tap to exit". One failure, two different
  //      instructions, neither of them the whole story.
  //   2. The toast's own advice was impossible to follow. The overlay covers
  //      the pill (inset 0) and contentEditable went false, so "type instead"
  //      was exactly what she could not do until she found the tap target.
  //   3. The star's label lied. It read "Exit voice mode" while toggle()
  //      treats "error" like "idle" and calls connect() — the button labelled
  //      Exit retried instead.
  //
  // Excluding "error" fixes all three at once: the toast is the single
  // surface, the pill goes straight back to typeable, and the star returns to
  // "Start voice conversation" — which is what tapping it actually does.
  //
  // The hook KEEPS its terminal error state; this is only about what the input
  // bar shows. errorStage still drives reporting, and toggle() from "error"
  // still retries.
  const isVoiceModeOn =
    voiceModeState === "connecting" ||
    voiceModeState === "live" ||
    voiceModeState === "ending";
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [imageDataList, setImageDataList] = useState<{ base64: string; mediaType: string }[]>([]);
  const [imageError, setImageError] = useState<string | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Log ChatInput's rendered position for the KB debug strip.
  // Keep this useLayoutEffect — removing similar logging broke things before (May 5).
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    console.log("[ChatInput rect]", {
      h: r.height,
      top: r.top,
      bottom: r.bottom,
      kbOpen: keyboardOpen,
      pill: showNewChat,
      imageCount: imagePreviews.length,
    });
    // Same guard useStableKeyboardOffset.ts:75 and useHideKeyboardAccessory.ts:71
    // already carry, and for the same reason — this site was simply missed when
    // they were fixed (Aug 20 2026). A bare `__kbDiag.chatinput = …` is a
    // TypeError whenever the global is absent, thrown from inside a layout
    // effect, which takes the whole input bar down rather than losing a debug
    // line. Latent in the app only because the keyboard hooks happen to create
    // the object first; nothing guarantees that ordering.
    const w = window as any;
    w.__kbDiag = w.__kbDiag || {};
    w.__kbDiag.chatinput = {
      propOpen: keyboardOpen,
      rectTop: Math.round(r.top),
      rectBottom: Math.round(r.bottom),
    };
  }, [keyboardOpen, imagePreviews.length, showNewChat]);

  useImperativeHandle(ref, () => ({
    focus: () => editorRef.current?.focus(),
    setText: (text: string) => {
      const el = editorRef.current;
      if (!el) return;
      el.innerText = text;
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 160) + "px";
      setIsEmpty(text.length === 0);
      // ⚠️ DO NOT add el.focus() back here. Issue #654.
      //
      // This method has exactly one caller: the cross-tab prefill in
      // Index.tsx ("Ask Kin for recipe" from Tasks). It runs from a
      // setTimeout ~100ms after a tab switch, so it is NOT inside a user
      // gesture — and WKWebView will not raise the keyboard for a
      // programmatic focus outside one.
      //
      // The focus therefore did two things, both bad:
      //
      //   1. It put a caret in a box with no keyboard under it.
      //   2. It started the silence watchdog in useKeyboardOffset.ts, whose
      //      premise is "a focus is the only thing that opens the keyboard in
      //      this app, so it's the correct moment to start expecting a native
      //      event." No keyboard opened, so Median sent no event, so ~1s
      //      later the watchdog concluded the native bridge was broken and
      //      armed the visualViewport fallback that a0b7271 rejected for
      //      WKWebView. That is the mid-screen floating pill, the hidden nav
      //      bar and the dead zone with no keyboard in it.
      //
      // If the keyboard genuinely needs to open here one day, it has to be
      // driven by the tap that started the navigation, not by a timer.
    },
  }));

  const getTextContent = () => editorRef.current?.innerText?.trim() ?? "";

  // handleInput auto-resize is load-bearing — do not modify
  const handleInput = useCallback(() => {
    const text = getTextContent();
    setIsEmpty(text.length === 0);
    const el = editorRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 160) + "px";
    }
  }, []);

  const clearEditor = () => {
    if (editorRef.current) {
      editorRef.current.innerText = "";
      editorRef.current.style.height = "auto";
    }
    setIsEmpty(true);
    setImagePreviews([]);
    setImageDataList([]);
    setImageError(null);
  };

  const handleSend = () => {
    const trimmed = getTextContent();
    const hasImages = imageDataList.length > 0;
    if ((!trimmed && !hasImages) || disabled) return;
    trackFeature("message_sent", { has_image: hasImages, image_count: imageDataList.length, length: trimmed.length });
    trackMessageForDau();
    const lower = trimmed.toLowerCase();
    if (/\b(i\s*(just\s*)?need\s*to\s*vent|let\s*me\s*vent|gonna\s*vent)\b/.test(lower)) {
      trackFeature("vent_mode", { source: "typed" });
    }
    if (/brain\s*dump/.test(lower)) {
      trackFeature("brain_dump", { source: "typed" });
    }
    // Back-compat: keep the first image in the existing single arg; pass the
    // full list as the opt-in third arg (mirrors MyKinTab's onAnalyze allImages).
    onSend(trimmed, imageDataList[0] ?? undefined, hasImages ? imageDataList : undefined);
    clearEditor();
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Reset so the same file can be re-picked after a remove.
    e.target.value = "";
    if (files.length === 0) return;

    setImageError(null);

    const currentCount = imageDataList.length;
    const remainingSlots = MAX_IMAGES - currentCount;
    if (remainingSlots <= 0) {
      toast(`You can add up to ${MAX_IMAGES} photos at a time 💜`);
      return;
    }

    // Accept up to the cap; toast if the selection overflowed.
    let filesToAdd = files;
    if (files.length > remainingSlots) {
      filesToAdd = files.slice(0, remainingSlots);
      toast(`You can add up to ${MAX_IMAGES} photos at a time 💜`);
    }

    const newData: { base64: string; mediaType: string }[] = [];
    const newPreviews: string[] = [];
    let runningTotal = imageDataList.reduce((sum, img) => sum + img.base64.length, 0);
    let hitPayloadCap = false;

    for (const file of filesToAdd) {
      if (file.size > MAX_IMAGE_SIZE) {
        // 5MB per-file backstop — skip this one, keep the rest.
        setImageError("That image is a little too big for me, babe. Try a smaller one or take a new photo. 💜");
        continue;
      }
      const { base64, mediaType, dataUrl } = await downscaleImage(file);
      // Total payload backstop — reject the last file(s) that would push us over.
      if (runningTotal + base64.length > MAX_TOTAL_BASE64) {
        hitPayloadCap = true;
        break;
      }
      runningTotal += base64.length;
      newData.push({ base64, mediaType });
      newPreviews.push(dataUrl);
    }

    if (hitPayloadCap) {
      setImageError("Those photos are a little too big together — try sending a few at a time 💜");
    }

    if (newData.length > 0) {
      setImageDataList((prev) => [...prev, ...newData]);
      setImagePreviews((prev) => [...prev, ...newPreviews]);
    }
  };

  const removeImage = (index: number) => {
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
    setImageDataList((prev) => prev.filter((_, i) => i !== index));
    setImageError(null);
  };

  // Handle one-shot mic tap: toggle recording → insert transcript into editor
  const handleMicTap = useCallback(async () => {
    if (voiceInput.isRecording) {
      const transcript = await voiceInput.stopRecording();
      if (transcript) {
        const el = editorRef.current;
        if (el) {
          const existing = el.innerText?.trim() ?? "";
          el.innerText = existing ? `${existing} ${transcript}` : transcript;
          el.style.height = "auto";
          el.style.height = Math.min(el.scrollHeight, 160) + "px";
          setIsEmpty(false);
          // ⚠️ DO NOT add el.focus() back here. Issue #1059, same trap as #654.
          //
          // This runs after `await voiceInput.stopRecording()`, which resolves
          // only once the recording has gone to the transcription endpoint and
          // come back — seconds after the tap, so NOT inside a user gesture, and
          // WKWebView will not raise the keyboard for a programmatic focus
          // outside one. The focus could therefore never help:
          //
          //   • keyboard still open when the transcript lands — the editor is
          //     already focused, so focus() adds nothing;
          //   • keyboard closed (never opened, or the taps closed it) — focus()
          //     cannot open it. It only puts a caret in a box with no keyboard
          //     under it, and starts the silence watchdog in
          //     useKeyboardOffset.ts. If Median has not delivered a
          //     keyboard event yet this session (a voice-first user who has not
          //     typed since launch), ~1s of legitimate silence reads as a broken
          //     bridge and arms the visualViewport fallback a0b7271 rejected:
          //     the pill floats mid-screen, the nav bar hides, and a
          //     keyboard-sized dead zone opens with no keyboard in it.
          //
          // The transcript lands in the box either way; one tap on it opens the
          // keyboard from a real gesture, which is the only thing that can.
        }
      }
    } else {
      await voiceInput.startRecording();
    }
  }, [voiceInput]);

  const isActive = (!isEmpty || imageDataList.length > 0) && !disabled;

  // Mic icon is inside the pill — Pro only, not during voice mode.
  // Stays visible while recording or processing even if user started typing,
  // but disappears the moment they start typing (if not recording/processing).
  const showMicInInput = isProUser && !limitReached && !isVoiceModeOn &&
    (isEmpty || voiceInput.isRecording || voiceInput.isProcessing);

  // Right slot shows star when: Pro + (voice mode active OR empty input with no image attached)
  // Right slot shows send when: has text/image, OR free user, OR limitReached override
  const showStarInSlot = isProUser && !limitReached && (isVoiceModeOn || (isEmpty && imageDataList.length === 0));

  // The action slot (stop / star / send) lives INSIDE the pill now, so the pill
  // spans the full freed width. Reserve right padding for whatever the
  // right-edge cluster is currently showing so typed text never slides under it.
  // Cluster geometry: right offset 6 + mic 32 + gap 2 + action 44 (+4 breathing).
  const showActionSlot = !limitReached;
  const pillRightPadding = showActionSlot
    ? (showMicInInput ? 88 : 54)
    : 18;

  return (
    <div
      ref={wrapperRef}
      data-chat-input
      style={{
        // Flexbox layout: this component is flex-shrink-0 at the bottom of the
        // chat column in Index.tsx. No fixed positioning, no top/vvBottom math.
        flexShrink: 0,
        width: "100%",
        backgroundColor: "#F0F0FF",
        paddingTop: 10,
        // Column's paddingBottom reserves calc(72px + safe-area) = 106px for the
        // nav bar. NavBar actual height = 8(paddingTop)+53(buttons)+23(footer)+34(safe-area)
        // = 118px. Gap = (106+paddingBottom) - 118. At 10px gap was -2px (pill clipped).
        // At 20px gap = 8px — just enough breathing room above the nav bar top.
        paddingBottom: 20,
      }}
    >
      {showNewChat && (
        <div className="max-w-2xl mx-auto flex justify-center mb-2 px-4">
          <button
            onClick={onNewChat}
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-body font-semibold text-kin-periwinkle
              bg-kin-soft-violet/60 hover:bg-kin-soft-violet border border-kin-periwinkle/20
              rounded-full transition-all duration-200 hover:scale-[1.03]"
          >
            <span className="text-[10px]">✦</span> New conversation
          </button>
        </div>
      )}

      {/* Image previews — horizontally scrollable row, one X per thumbnail.
          Stays above the pill; does not touch pill geometry. */}
      {imagePreviews.length > 0 && (
        <div className="max-w-2xl mx-auto mb-2 px-4">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {imagePreviews.map((preview, index) => (
              <div key={index} className="relative flex-shrink-0">
                <img
                  src={preview}
                  alt={`Upload preview ${index + 1}`}
                  className="w-[80px] h-[80px] rounded-[10px] object-cover"
                />
                <button
                  onClick={() => removeImage(index)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-kin-periwinkle text-primary-foreground flex items-center justify-center"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Image error */}
      {imageError && (
        <div className="max-w-2xl mx-auto mb-2 px-4">
          <p className="text-xs font-body text-destructive">{imageError}</p>
        </div>
      )}

      <div
        style={{
          display: "flex",
          // flex-end, not center: the pill grows upward on multiline, so the
          // camera stays level with the in-pill buttons at the bottom instead
          // of floating to the middle of a tall pill.
          alignItems: "flex-end",
          gap: 10,
          maxWidth: "42rem",
          margin: "0 auto",
          paddingLeft: 16,
          paddingRight: 16,
          width: "100%",
        }}
      >
        {/* Camera button — still outside on the left, hidden during voice
            conversation mode. height 44 (was 40) so its icon lines up with the
            in-pill buttons now that the row is bottom-aligned. */}
        {!limitReached && !isVoiceModeOn && (
          <>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={limitReached}
              aria-label="Add photo"
              className="text-kin-periwinkle/70 hover:text-kin-periwinkle transition-colors disabled:opacity-40"
              style={{
                flexShrink: 0,
                width: 40,
                height: 44,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              <Camera size={20} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </>
        )}

        {/* Text input pill — stretches to the row's full width (no outer right
            slot anymore); mic + stop/star/send sit inside its right edge */}
        <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
          <div
            ref={editorRef}
            inputMode="text"
            role="textbox"
            aria-multiline="true"
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            contentEditable={!limitReached && !isVoiceModeOn}
            tabIndex={-1}
            data-placeholder={limitReached ? "Come back tomorrow 💜" : "Ask Kin anything..."}
            autoCorrect="on"
            autoCapitalize="sentences"
            spellCheck={true}
            aria-disabled={limitReached}
            className={`max-h-[160px] overflow-y-auto resize-none
              empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground empty:before:pointer-events-none empty:before:whitespace-nowrap empty:before:overflow-hidden empty:before:text-ellipsis empty:before:block empty:before:text-sm
              focus:outline-none focus:border-kin-periwinkle focus:ring-[3px] focus:ring-kin-periwinkle/20
              transition-all duration-200 [-webkit-touch-callout:none] [-webkit-user-modify:read-write-plaintext-only]
              ${limitReached ? "opacity-60 cursor-not-allowed select-none" : ""}`}
            style={{
              width: "100%",
              minHeight: 44,
              paddingLeft: 18,
              // Extra right padding reserves space so typed text never slides
              // under the in-pill control cluster (mic + stop/star/send)
              paddingRight: pillRightPadding,
              paddingTop: 11,
              paddingBottom: 11,
              borderRadius: 20,
              border: "1px solid #E8E6F4",
              backgroundColor: "#FFFFFF",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 16,
              lineHeight: 1.45,
              color: "#1C1A3E",
              outline: "none",
            }}
          />

          {/* Right-edge control cluster — mic + action button live INSIDE the pill
              (ChatGPT/Claude-style), so the pill stretches the full freed width.
              bottom-anchored (not vertically centered) so the buttons stay pinned
              to the bottom of the pill as it grows on multiline instead of
              drifting downward with the middle. zIndex 2 keeps the action button
              tappable above the voice-mode overlay (zIndex 1). */}
          <div
            style={{
              position: "absolute",
              right: 6,
              bottom: 1,
              display: "flex",
              alignItems: "center",
              gap: 2,
              zIndex: 2,
            }}
          >
            {/* Inline mic icon — Pro only. Disappears when user starts typing;
                stays visible while recording/processing. */}
            {showMicInInput && (
              <button
                onClick={handleMicTap}
                aria-label={voiceInput.isRecording ? "Stop recording" : "Voice input"}
                style={{
                  flexShrink: 0,
                  width: 32,
                  height: 44,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
                className={voiceInput.isRecording ? "animate-pulse" : ""}
              >
                {voiceInput.isProcessing ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="animate-spin"
                    style={{ color: "#5050C8" }}>
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"
                      strokeDasharray="31.4" strokeDashoffset="10" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    style={{ color: voiceInput.isRecording ? "#E53E3E" : "#54525C" }}>
                    <rect x="9" y="2" width="6" height="12" rx="3" fill="currentColor" />
                    <path d="M5 10a7 7 0 0 0 14 0" stroke="currentColor" strokeWidth="1.8"
                      strokeLinecap="round" fill="none" />
                    <line x1="12" y1="19" x2="12" y2="22" stroke="currentColor"
                      strokeWidth="1.8" strokeLinecap="round" />
                    <line x1="9" y1="22" x2="15" y2="22" stroke="currentColor"
                      strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                )}
              </button>
            )}

            {/* Merged action slot — exclusive state, first match wins:
                1. isStreaming + !voice → Stop (red square)
                2. showStarInSlot      → Star (voice mode lifecycle or empty+Pro)
                3. default             → Send arrow
                Each button keeps a 44px tap target; the visible chrome is a 34px
                inner box so it sits inside the pill instead of filling it. */}
            {showActionSlot && (
              <div style={{ position: "relative", width: 44, height: 44, flexShrink: 0 }}>

                {isStreaming && !isVoiceModeOn ? (
                  /* Priority 1 — Stop: abort streaming, wins over star/send */
                  <button
                    onClick={onStop}
                    aria-label="Stop generating"
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "none",
                      border: "none",
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      className="hover:scale-[1.05] active:scale-[0.95]"
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 11,
                        backgroundColor: "#FEE2E2",
                        border: "1.5px solid #E05050",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <div className="w-3 h-3 rounded-[2px]" style={{ backgroundColor: "#E05050" }} />
                    </div>
                  </button>
                ) : showStarInSlot ? (
                  /* Priority 2 — Star: voice mode active or empty input + Pro */
                  <button
                    onClick={onVoiceModeToggle}
                    aria-label={isVoiceModeOn ? "Exit voice mode" : "Start voice conversation"}
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "none",
                      border: "none",
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                    className={voiceModeState === "connecting" ? "animate-pulse" : ""}
                  >
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 11,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: isVoiceModeOn ? "#5050C8" : "transparent",
                        transition: "background-color 0.2s ease, box-shadow 0.2s ease",
                        boxShadow: voiceModeState === "connecting"
                          ? "0 0 0 5px rgba(80,80,200,0.25)"
                          : "none",
                      }}
                    >
                      <span style={{
                        fontSize: 20, lineHeight: 1,
                        color: isVoiceModeOn ? "white" : "#54525C",
                      }}>✦</span>
                    </div>
                  </button>
                ) : (
                  /* Priority 3 — Send: has text/image, or free user */
                  <button
                    onClick={handleSend}
                    aria-disabled={!isActive}
                    aria-label="Send message"
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "none",
                      border: "none",
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      className={`transition-all duration-200 flex items-center justify-center
                        ${isActive
                          ? "bg-gradient-to-br from-kin-periwinkle to-kin-charcoal hover:scale-[1.08] hover:shadow-kin-elevated cursor-pointer"
                          : "cursor-not-allowed"
                        }`}
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 11,
                        ...(isActive ? {} : { backgroundColor: "#C8C8F4" }),
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 20 20" fill="none" className="text-primary-foreground">
                        <path
                          d="M3.5 10L16.5 3.5L10 16.5L8.5 11.5L3.5 10Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Voice mode status overlay — tap to exit voice mode and return to typing */}
          {isVoiceModeOn && (
            <div
              onClick={onDeactivateVoiceMode}
              title="Tap to exit voice mode and type instead"
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: 20,
                border: "1px solid #C8C8F4",
                backgroundColor: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                zIndex: 1,
              }}
            >
              <span
                style={{
                  fontStyle: "italic",
                  fontSize: 15,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  // No "error" case here. This overlay renders only while
                  // isVoiceModeOn — connecting/live/ending — so "error" is
                  // unreachable inside it, which is the point of the Sep 2 2026
                  // fix pinned by chatInputVoiceErrorState.test.tsx: by the time
                  // the hook reports "error" it has already torn everything
                  // down, so there is nothing to exit from and the overlay must
                  // not cover the pill. The red branch and the "Voice hiccupped
                  // — tap to exit" string were left behind by that fix; they
                  // were dead code, and a TS2367 that nothing in CI read (#1360).
                  color: voiceModeState === "ending" ? "#A0A0B8" : "#5050C8",
                }}
              >
                {voiceModeState === "connecting" && "Connecting..."}
                {voiceModeState === "live" && "Kin is listening..."}
                {voiceModeState === "ending" && "..."}
              </span>
            </div>
          )}
        </div>

      </div>
    </div>
  );
});

ChatInput.displayName = "ChatInput";

export default ChatInput;
